import { formatDistanceToNowStrict } from 'date-fns';

import type { InquiryV2 } from '@/services/getInquiriesV2';
import { getProfileNameAndInitials } from '@/util/getProfileNameAndInitials';
import {
  getAnonymousPrimumRoutedTitleExpert,
  isAskAnonymouslyAuthoredInquiry,
} from '@/util/inquiryDisplay';
import { getGroupTabLabel, getInquiryParentKey } from '@/util/inquiryGroups';
import { isAskAnonymously } from '@/util/userFilters';

/**
 * An inquiry group is an original inquiry together with the expert relay threads sent out from it.
 *
 * Grouping runs on the client over the rows a tab has already loaded, so it can only attach what
 * it can see. A relay whose original is not loaded is left in `remaining` as an ordinary row, so a
 * thread is never hidden. An original whose relays sit on a later page shows only the loaded ones,
 * and an inquiry group can split across pages until grouping moves to the server.
 */
export type QuestionGroup = {
  /**
   * `question-group-` and the original's conversation id, so the server's group and the client's own
   * for the same question share one key and keep their expanded state when the list switches between
   * them. The server's entry key stands in only for a group with no original at all.
   */
  key: string;
  original: InquiryV2;
  /**
   * True when the original was fetched on its own rather than found in the loaded rows. It has no
   * place in the API's order, so it never decides where the inquiry group sits in the list.
   */
  originalFetched?: boolean;
  /**
   * The submitter's own threads for this question: the original and the loaded siblings of the
   * fan-out it came from. The inquiry group consumes all of them, so no sibling is left behind as a flat
   * row. Always holds the original.
   */
  originalGroup: InquiryV2[];
  /** Relay threads in the order they were sent. */
  threads: InquiryV2[];
  /**
   * Experts the submitter picked that nobody has relayed to yet. Each stands for the submitter's own
   * fan-out thread that carries the pick, so it opens like any thread. The server sends them in its
   * own order; client grouping finds them from the picks on the loaded fan-out threads.
   */
  notSentExperts?: NotSentExpert[];
};

export type NotSentExpert = {
  id: number;
  name: string;
  lastName: string;
  /** The submitter's fan-out thread that carries the pick. */
  thread: InquiryV2;
};

/** The one key format for a group, in server and client grouping alike. */
export function getQuestionGroupKey(identity: string) {
  return `question-group-${identity}`;
}

export type QuestionGroupSplit = {
  questionGroups: QuestionGroup[];
  /** Everything that is not part of an inquiry group, in its incoming order. */
  remaining: InquiryV2[];
};

export type QuestionGroupThreadStatus = 'open' | 'close_requested' | 'closed';

export const ANONYMOUS_SUBMITTER_LABEL = 'Anonymous submitter';

export const TITLE_SEPARATOR = ' · ';

/**
 * A relay is the expert-side thread the Ask Anonymously account forwarded a question into, and it
 * points back at the original through `referenceInquiryIds`. Other inquiries also carry reference
 * ids, such as a follow-up linked to an earlier question, so the author test is what keeps those
 * out of an inquiry group.
 */
export function isRelayThread(inquiry: InquiryV2): boolean {
  return isAskAnonymouslyAuthoredInquiry(inquiry) && (inquiry.referenceInquiryIds?.length ?? 0) > 0;
}

function toTimestamp(value?: string | null) {
  return parseThreadTimestamp(value).getTime();
}

/** Oldest first, by the time each thread was sent, then by id. */
export function compareSentOrder(left: InquiryV2, right: InquiryV2) {
  const sentDiff = toTimestamp(left.createdDate) - toTimestamp(right.createdDate);

  if (sentDiff !== 0) {
    return sentDiff;
  }

  return left.id - right.id;
}

/**
 * The experts the submitter picked on these fan-out threads that nobody has relayed to yet, the
 * client's reading of the server's Not sent rule: a pick counts while the thread carrying it is not
 * closed, names an expert rather than the routing account, and has no relay to that expert among
 * `relays`. One row per expert, in the order the threads were sent.
 */
export function findNotSentExperts(
  fanOutThreads: readonly InquiryV2[],
  relays: readonly InquiryV2[]
): NotSentExpert[] {
  const relayedIds = new Set(relays.map((relay) => relay.recipient?.id));

  const seen = new Set<number>();

  return [...fanOutThreads].sort(compareSentOrder).flatMap((thread): NotSentExpert[] => {
    const expert = getAnonymousPrimumRoutedTitleExpert(thread);

    if (!expert || getThreadStatus(thread) === 'closed') {
      return [];
    }

    const name = getProfileNameAndInitials({
      firstName: expert.firstName,
      lastName: expert.lastName,
      identifiesAsDoctor: expert.identifiesAsDoctor,
    }).name;

    if (isAskAnonymously(name) || relayedIds.has(expert.id) || seen.has(expert.id)) {
      return [];
    }

    seen.add(expert.id);

    return [
      {
        id: expert.id,
        name,
        lastName: expert.lastName?.trim() || expert.firstName?.trim() || '',
        thread,
      },
    ];
  });
}

/**
 * The thread a fan-out set's card is titled from: the root the siblings point at when it is loaded,
 * otherwise the first one sent.
 */
function pickFanOutOriginal(fanOutThreads: readonly InquiryV2[]) {
  return (
    fanOutThreads.find((thread) => thread.parentId === null || thread.parentId === undefined) ??
    [...fanOutThreads].sort(compareSentOrder)[0]
  );
}

/**
 * Splits a tab's loaded inquiries into inquiry groups and everything else.
 *
 * A relay attaches to the first of its referenced ids that resolves to a loaded inquiry that is
 * not itself a relay. A fan-out the submitter sent with experts picked forms an inquiry group even
 * before anyone relays it, with each pick still waiting shown as Not sent, as the server does. An
 * original with no relay and no pick waiting is not an inquiry group, so it stays a flat row.
 */
export function buildQuestionGroups(
  inquiries: readonly InquiryV2[],
  fetchedOriginals: readonly InquiryV2[] = []
): QuestionGroupSplit {
  const byConversationId = new Map(inquiries.map((inquiry) => [inquiry.conversationId, inquiry]));

  const loadedIds = new Set(byConversationId.keys());

  // A fetched original that has since loaded in the page is the loaded one, so it never doubles.
  fetchedOriginals.forEach((original) => {
    if (!byConversationId.has(original.conversationId)) {
      byConversationId.set(original.conversationId, original);
    }
  });

  const threadsByOriginal = new Map<string, InquiryV2[]>();

  inquiries.forEach((inquiry) => {
    if (!isRelayThread(inquiry)) {
      return;
    }

    const originalId = inquiry.referenceInquiryIds.find((referenceId) => {
      const candidate = byConversationId.get(referenceId);

      return candidate !== undefined && !isRelayThread(candidate);
    });

    if (!originalId) {
      return;
    }

    const threads = threadsByOriginal.get(originalId) ?? [];

    threads.push(inquiry);

    threadsByOriginal.set(originalId, threads);
  });

  const consumed = new Set<string>();

  const questionGroups: QuestionGroup[] = [];

  threadsByOriginal.forEach((threads, originalId) => {
    const original = byConversationId.get(originalId);

    if (!original) {
      return;
    }

    const parentKey = getInquiryParentKey(original);

    const siblings = inquiries.filter(
      (inquiry) =>
        inquiry.conversationId !== originalId &&
        !isRelayThread(inquiry) &&
        !consumed.has(inquiry.conversationId) &&
        getInquiryParentKey(inquiry) === parentKey
    );

    consumed.add(originalId);

    siblings.forEach((sibling) => consumed.add(sibling.conversationId));

    threads.forEach((thread) => consumed.add(thread.conversationId));

    const originalGroup = [original, ...siblings];

    const notSentExperts = findNotSentExperts(originalGroup, threads);

    questionGroups.push({
      key: getQuestionGroupKey(originalId),
      original,
      originalFetched: !loadedIds.has(originalId),
      originalGroup,
      threads: [...threads].sort(compareSentOrder),
      ...(notSentExperts.length ? { notSentExperts } : {}),
    });
  });

  const unrelayedFanOuts = new Map<number, InquiryV2[]>();

  inquiries.forEach((inquiry) => {
    if (consumed.has(inquiry.conversationId) || isRelayThread(inquiry)) {
      return;
    }

    const parentKey = getInquiryParentKey(inquiry);

    unrelayedFanOuts.set(parentKey, [...(unrelayedFanOuts.get(parentKey) ?? []), inquiry]);
  });

  unrelayedFanOuts.forEach((fanOutThreads) => {
    const notSentExperts = findNotSentExperts(fanOutThreads, []);

    if (!notSentExperts.length) {
      return;
    }

    const original = pickFanOutOriginal(fanOutThreads);

    fanOutThreads.forEach((thread) => consumed.add(thread.conversationId));

    questionGroups.push({
      key: getQuestionGroupKey(original.conversationId),
      original,
      originalGroup: [original, ...fanOutThreads.filter((thread) => thread !== original)],
      threads: [],
      notSentExperts,
    });
  });

  return {
    questionGroups,
    remaining: inquiries.filter((inquiry) => !consumed.has(inquiry.conversationId)),
  };
}

/**
 * The conversation ids of originals to fetch: for each relay none of whose referenced inquiries is
 * loaded, the first reference. Each id appears once, in the order the relays appear.
 */
export function findMissingOriginalIds(inquiries: readonly InquiryV2[]): string[] {
  const loadedIds = new Set(inquiries.map((inquiry) => inquiry.conversationId));

  const missing = new Set<string>();

  inquiries.forEach((inquiry) => {
    if (!isRelayThread(inquiry)) {
      return;
    }

    if (inquiry.referenceInquiryIds.some((referenceId) => loadedIds.has(referenceId))) {
      return;
    }

    missing.add(inquiry.referenceInquiryIds[0]);
  });

  return [...missing];
}

/**
 * Which of the wanted ids may be requested now. Settled ids never count against the limit, so the
 * list works through a long backlog a few requests at a time instead of all at once.
 */
export function selectOriginalIdsToFetch(
  wantedIds: readonly string[],
  isSettled: (conversationId: string) => boolean,
  maxInFlight: number
): Set<string> {
  const allowed = new Set<string>();

  let unsettled = 0;

  wantedIds.forEach((conversationId) => {
    if (isSettled(conversationId)) {
      allowed.add(conversationId);

      return;
    }

    if (unsettled < maxInFlight) {
      allowed.add(conversationId);

      unsettled += 1;
    }
  });

  return allowed;
}

/** Every conversation an inquiry group holds, for the selected-row test and the chat's sibling tabs. */
export function getQuestionGroupConversationIds(questionGroup: QuestionGroup) {
  return [
    ...questionGroup.originalGroup.map((inquiry) => inquiry.conversationId),
    ...questionGroup.threads.map((thread) => thread.conversationId),
    ...(questionGroup.notSentExperts ?? []).map((expert) => expert.thread.conversationId),
  ];
}

export function getThreadStatus(thread: InquiryV2): QuestionGroupThreadStatus {
  if (thread.inquiryStatus === 'closed') {
    return 'closed';
  }

  if (thread.inquiryStatus === 'inactive') {
    return 'close_requested';
  }

  return 'open';
}

/** True while the expert has not written back. `firstRespondedAt` is set once, on the first reply. */
export function isThreadWaiting(thread: InquiryV2) {
  return getThreadStatus(thread) === 'open' && !thread.firstRespondedAt;
}

/**
 * Postgres text, "2026-10-28 22:42:33+00", rewritten as ISO 8601, "2026-10-28T22:42:33+00:00":
 * the space becomes a T, and an hour-only offset after a time gains its minutes. Anything else,
 * ISO and a bare date included, comes back unchanged.
 */
export function toIsoTimestamp(value: string) {
  return value.replace(
    /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}(?:\.\d+)?)([+-]\d{2})?$/,
    (_match, day: string, time: string, offset?: string) =>
      `${day}T${time}${offset ? `${offset}:00` : ''}`
  );
}

/**
 * A thread timestamp as a Date, from the first of `values` that parses.
 *
 * The API sends some timestamps as ISO strings and `lastMessageAt` as Postgres text,
 * "2026-10-28 22:42:33+00". A browser parses both, but Hermes on iOS and Android parses only ISO, so
 * the Postgres form came back Invalid Date there and the card's "3 hours ago" threw and took the
 * whole inbox down. The space and the hour-only offset are rewritten into ISO before parsing, and a
 * value that still does not parse is skipped rather than returned.
 */
export function parseThreadTimestamp(...values: (string | null | undefined)[]): Date {
  for (const value of values) {
    if (!value) {
      continue;
    }

    const date = new Date(toIsoTimestamp(value));

    if (!Number.isNaN(date.getTime())) {
      return date;
    }
  }

  return new Date(0);
}

export function getThreadSentAt(thread: InquiryV2) {
  return parseThreadTimestamp(thread.createdDate, thread.lastResponseDate);
}

export function getThreadActivityAt(thread: InquiryV2) {
  return parseThreadTimestamp(thread.lastMessageAt, thread.lastResponseDate, thread.createdDate);
}

export function getThreadExpertName(thread: InquiryV2) {
  return getProfileNameAndInitials({
    firstName: thread.recipient?.firstName,
    lastName: thread.recipient?.lastName,
    identifiesAsDoctor: thread.recipient?.identifiesAsDoctor,
  }).name;
}

/**
 * "3 hours ago", in the wording the app's comments use. Empty for an invalid date, since
 * date-fns throws on one and a card's time line is not worth the inbox.
 */
export function formatAgo(date: Date) {
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return formatDistanceToNowStrict(date, { addSuffix: true });
}

function newestBy(threads: readonly InquiryV2[], getDate: (thread: InquiryV2) => Date) {
  return threads.reduce<InquiryV2 | undefined>(
    (newest, thread) =>
      !newest || getDate(thread).getTime() > getDate(newest).getTime() ? thread : newest,
    undefined
  );
}

function oldestBy(threads: readonly InquiryV2[], getDate: (thread: InquiryV2) => Date) {
  return threads.reduce<InquiryV2 | undefined>(
    (oldest, thread) =>
      !oldest || getDate(thread).getTime() < getDate(oldest).getTime() ? thread : oldest,
    undefined
  );
}

/**
 * One row of an open card: an expert's thread, a picked expert's fan-out thread not yet sent, or
 * the submitter's own thread with Ask Anonymously while it holds an unread message.
 */
export type QuestionGroupRow = {
  thread: InquiryV2;
  name: string;
  lastName: string;
  notSent: boolean;
  /**
   * True when the thread is the submitter's with Ask Anonymously rather than one addressed to an
   * expert: every not-sent row, and the submitter's own row.
   */
  fromSubmitter: boolean;
  /**
   * The expert's photo. A sent row's thread is addressed to the expert; a not-sent row's thread is
   * the submitter's, addressed to the routing account, so its photo comes from the pick it carries.
   */
  avatar: NonNullable<InquiryV2['recipient']>['avatar'] | null;
};

function getPersonName(person: InquiryV2['author'] | null | undefined) {
  if (!person) {
    return '';
  }

  return getProfileNameAndInitials({
    firstName: person.firstName,
    lastName: person.lastName,
    identifiesAsDoctor: person.identifiesAsDoctor,
  }).name;
}

function toSubmitterRow(thread: InquiryV2): QuestionGroupRow {
  const submitter = thread.attributedSubmitter ?? thread.author;

  const name = getPersonName(submitter);

  return {
    thread,
    name,
    lastName: submitter?.lastName?.trim() || name,
    notSent: false,
    fromSubmitter: true,
    avatar: submitter?.avatar ?? null,
  };
}

/**
 * The card's rows: the expert threads in order, then the picked experts nobody has been sent to.
 * A card never lists more rows than experts, since a fourth row on a three-expert card reads as a
 * fourth expert.
 */
export function getQuestionGroupRows(
  questionGroup: Pick<QuestionGroup, 'threads' | 'notSentExperts'>
): QuestionGroupRow[] {
  const notSentRows = (questionGroup.notSentExperts ?? []).map((expert) => ({
    thread: expert.thread,
    name: expert.name,
    lastName: expert.lastName,
    notSent: true,
    fromSubmitter: true,
    avatar: getAnonymousPrimumRoutedTitleExpert(expert.thread)?.avatar ?? null,
  }));

  return [
    ...questionGroup.threads.map((thread) => ({
      thread,
      name: getThreadExpertName(thread),
      lastName: getThreadLastName(thread),
      notSent: false,
      fromSubmitter: false,
      avatar: thread.recipient?.avatar ?? null,
    })),
    ...notSentRows,
  ];
}

/**
 * The submitter's threads with Ask Anonymously that hold an unread message once the question is
 * with experts, such as a follow-up the submitter wrote after the relays went out.
 *
 * They have no row, so the card header carries them: its dot and unread count, and a tap on the
 * header of an open card. Without that a follow-up would leave the card looking read. Before a
 * relay the submitter's threads are the rows already, so there is nothing to carry.
 */
export function getUnreadSubmitterThreads(
  questionGroup: Pick<QuestionGroup, 'threads' | 'notSentExperts' | 'originalGroup'>
): InquiryV2[] {
  if (!questionGroup.threads.length) {
    return [];
  }

  const rowIds = new Set(
    getQuestionGroupRows(questionGroup).map((row) => row.thread.conversationId)
  );

  return (questionGroup.originalGroup ?? []).filter(
    (thread) => thread.hasUnread && !rowIds.has(thread.conversationId)
  );
}

/**
 * What a tap on the inquiry group opens when it does not expand: the newest unread row's thread,
 * otherwise the newest by activity, with the conversations the chat shows as its sibling tabs. An
 * expert thread's tabs are the expert threads; a not-sent row is a fan-out thread, so its tabs are
 * the submitter's fan-out threads, as a flat tabbed group row opens them. A group with no row at all
 * opens the submitter's original the same way, so the target is never undefined.
 */
export function getQuestionGroupOpenTarget(
  questionGroup: Pick<QuestionGroup, 'original' | 'originalGroup' | 'threads' | 'notSentExperts'>
): { inquiry: InquiryV2; siblingConversationIds: string[] } {
  const fanOutIds = questionGroup.originalGroup.map((inquiry) => inquiry.conversationId);

  const rows = getQuestionGroupRows(questionGroup);

  const unread = [
    ...getUnreadSubmitterThreads(questionGroup).map(toSubmitterRow),
    ...rows.filter((row) => row.thread.hasUnread),
  ];

  const pool = unread.length ? unread : rows;

  const target = pool.reduce<QuestionGroupRow | undefined>(
    (newest, row) =>
      !newest ||
      getThreadActivityAt(row.thread).getTime() > getThreadActivityAt(newest.thread).getTime()
        ? row
        : newest,
    undefined
  );

  if (!target) {
    return { inquiry: questionGroup.original, siblingConversationIds: fanOutIds };
  }

  return {
    inquiry: target.thread,
    siblingConversationIds: target.fromSubmitter
      ? fanOutIds
      : questionGroup.threads.map((thread) => thread.conversationId),
  };
}

/** One chat tab for an expert thread of an inquiry group. */
export type QuestionGroupChatTab = {
  thread: InquiryV2;
  /** "Dr. Last" for an expert who identifies as a doctor, otherwise the full name. */
  label: string;
};

/**
 * The chat tabs for a conversation opened from an inquiry group: every expert thread the card
 * lists, in the order they were sent, when the conversation is one of them.
 *
 * Chat tabs otherwise come from one send, so experts relayed one at a time each opened alone
 * while the card held them together. Reading the card's own rows keeps the two in step.
 *
 * Undefined for the submitter's threads and the picks not yet sent, which keep the tabs of the
 * fan-out they belong to: in one strip the submitter's thread to an expert and the relay to the
 * same expert would carry the same name. Undefined too below two tabs, as there is nothing to
 * switch between.
 */
export function getQuestionGroupChatTabs(
  questionGroup: Pick<QuestionGroup, 'threads' | 'notSentExperts'> &
    Partial<Pick<QuestionGroup, 'originalGroup'>>,
  conversationId: string
): QuestionGroupChatTab[] | undefined {
  const expertRows = getQuestionGroupRows(questionGroup).filter((row) => !row.fromSubmitter);

  if (
    expertRows.length < 2 ||
    !expertRows.some((row) => row.thread.conversationId === conversationId)
  ) {
    return undefined;
  }

  return expertRows.map((row) => ({
    thread: row.thread,
    label: getGroupTabLabel({
      firstName: row.thread.recipient?.firstName,
      lastName: row.thread.recipient?.lastName,
      identifiesAsDoctor: row.thread.recipient?.identifiesAsDoctor,
    }),
  }));
}

export type QuestionGroupSummary = {
  unreadCount: number;
  /** Line 2 after "N unread": the age of the newest unread reply, or how long the longest wait is. */
  timeText: string;
  /** The words before the age, so the age alone can carry the exact-time tooltip. */
  timeLead: string;
  timeAgo: string;
  /** The date the time text counts from, for the exact-time tooltip. */
  timeAt: Date;
  /** "Expert: preview" for the thread whose reply a collapsed card shows, when it has text. */
  preview: string | null;
  /** The badge text for the exception states, or null when every thread is open. */
  closedLabel: string | null;
  closeRequestedLabel: string | null;
};

function describeCount(count: number, total: number, singular: string, plural: string) {
  if (count === 0) {
    return null;
  }

  return count === total ? singular : `${count} ${plural}`;
}

export function summarizeQuestionGroup(threads: readonly InquiryV2[]): QuestionGroupSummary {
  const unreadThreads = threads.filter((thread) => thread.hasUnread);

  const waitingThreads = threads.filter(isThreadWaiting);

  const repliedThreads = threads.filter((thread) => !isThreadWaiting(thread));

  const latestUnread = newestBy(unreadThreads, getThreadActivityAt);

  const longestWaiting = oldestBy(waitingThreads, getThreadSentAt);

  const newest = newestBy(threads, getThreadActivityAt);

  let timeLead = '';

  let timeAt = new Date(0);

  if (latestUnread) {
    timeAt = getThreadActivityAt(latestUnread);
  } else if (longestWaiting) {
    timeAt = getThreadSentAt(longestWaiting);

    timeLead = 'Sent ';
  } else if (newest) {
    timeAt = getThreadActivityAt(newest);
  }

  const timeAgo = formatAgo(timeAt);

  const previewThread = latestUnread ?? newestBy(repliedThreads, getThreadActivityAt);

  const previewText = previewThread?.lastMessagePreview?.trim();

  const preview =
    previewThread && previewText ? `${getThreadExpertName(previewThread)}: ${previewText}` : null;

  const closedCount = threads.filter((thread) => getThreadStatus(thread) === 'closed').length;

  const closeRequestedCount = threads.filter(
    (thread) => getThreadStatus(thread) === 'close_requested'
  ).length;

  return {
    unreadCount: unreadThreads.length,
    timeText: `${timeLead}${timeAgo}`,
    timeLead,
    timeAgo,
    timeAt,
    preview,
    closedLabel: describeCount(closedCount, threads.length, 'Closed', 'closed'),
    closeRequestedLabel: describeCount(
      closeRequestedCount,
      threads.length,
      'Close requested',
      'close requested'
    ),
  };
}

/** The expert's last name, which is how the inquiry group lists who holds it. */
export function getThreadLastName(thread: InquiryV2) {
  return thread.recipient?.lastName?.trim() || getThreadExpertName(thread);
}

/**
 * The experts an inquiry group was sent to, as last names: at most `max`, then "+N". The same name is not
 * listed twice, so two experts who share a surname show once.
 */
export function formatQuestionGroupNames(
  threads: readonly InquiryV2[],
  notSentExperts: readonly Pick<NotSentExpert, 'lastName'>[] = [],
  max = 3
) {
  const names = Array.from(
    new Set([...threads.map(getThreadLastName), ...notSentExperts.map((expert) => expert.lastName)])
  );

  const shown = names.slice(0, max);

  const extra = names.length - shown.length;

  return extra > 0 ? `${shown.join(', ')} +${extra}` : shown.join(', ');
}

/** A thread's latest message, only once the expert has written back. */
export function getThreadPreview(thread: InquiryV2) {
  if (isThreadWaiting(thread)) {
    return null;
  }

  return thread.lastMessagePreview?.trim() || null;
}

/**
 * The line under a row's name. An expert thread shows its latest reply once there is one. A picked
 * expert's row is the submitter's own thread with Ask Anonymously, so it shows the submitter's
 * latest message, attributed to the submitter so it never reads as the expert's. The submitter's
 * own row is already named for them, so its message needs no attribution.
 */
export function getRowPreview(row: Pick<QuestionGroupRow, 'thread' | 'notSent' | 'fromSubmitter'>) {
  if (!row.fromSubmitter) {
    return getThreadPreview(row.thread);
  }

  const message = row.thread.lastMessagePreview?.trim();

  if (!message) {
    return null;
  }

  if (!row.notSent) {
    return message;
  }

  // A pick's thread that holds only the question would repeat it on every pick's row; the row says
  // it is not relayed instead. A follow-up the submitter added to this pick still shows.
  if (row.thread.lastMessageIndex === 0) {
    return null;
  }

  const submitterName = getPersonName(row.thread.attributedSubmitter ?? row.thread.author);

  return submitterName ? `${submitterName}: ${message}` : message;
}

export type QuestionGroupBadge = { tone: 'unread' | 'muted'; label: string };

/**
 * The one badge on a collapsed inquiry group, the most important state first: something to read, then a
 * close request, then a closed thread, then an expert not sent to yet. An inquiry group where everyone is simply waiting has none.
 */
export function getQuestionGroupBadge(
  summary: Pick<QuestionGroupSummary, 'unreadCount' | 'closeRequestedLabel' | 'closedLabel'>
): QuestionGroupBadge | null {
  if (summary.unreadCount > 0) {
    return { tone: 'unread', label: `${summary.unreadCount} unread` };
  }

  if (summary.closeRequestedLabel) {
    return { tone: 'muted', label: summary.closeRequestedLabel };
  }

  if (summary.closedLabel) {
    return { tone: 'muted', label: summary.closedLabel };
  }

  return null;
}

/** A thread's second line: "Sent 1 day ago" while it waits, otherwise "preview, 3 hours ago". */
export function getThreadSecondLine(thread: InquiryV2) {
  if (isThreadWaiting(thread)) {
    const at = getThreadSentAt(thread);

    const ago = formatAgo(at);

    return { lead: 'Sent ', ago, at, text: `Sent ${ago}` };
  }

  const at = getThreadActivityAt(thread);

  const ago = formatAgo(at);

  const preview = thread.lastMessagePreview?.trim();

  const lead = preview ? `${preview}${TITLE_SEPARATOR}` : '';

  return { lead, ago, at, text: `${lead}${ago}` };
}

/**
 * The inquiry group title: cancer type, the submitter, then a short case line. It never carries an expert
 * name. The submitter's name belongs to a staff surface only, so callers gate the whole card on
 * staff before this runs. The card draws the case line on a line of its own under the heading and
 * uses the whole title as its spoken name.
 *
 * The list row carries `attributedSubmitter` only when support wrote on someone's behalf, so the
 * original's author stands in otherwise. With no usable name the title says so rather than
 * guessing. The case line is the written clinical case line, falling back to the diagnosis text,
 * and is left out when neither exists.
 */
export function buildQuestionGroupTitle(
  original: InquiryV2,
  originalGroup: readonly InquiryV2[] = [original]
) {
  return [buildQuestionGroupHeading(original), getQuestionGroupCaseLine(original, originalGroup)]
    .filter(Boolean)
    .join(TITLE_SEPARATOR);
}

/** The title's first line: cancer type and the submitter, without the case line. */
export function buildQuestionGroupHeading(original: InquiryV2) {
  const cancerLabel = original.cancerType?.shortLabel ?? original.cancerType?.label ?? '';

  const person = original.attributedSubmitter ?? original.author;

  const personName = person
    ? getProfileNameAndInitials({
        firstName: person.firstName,
        lastName: person.lastName,
        identifiesAsDoctor: person.identifiesAsDoctor,
      }).name
    : '';

  const submitter =
    personName && !isAskAnonymously(personName) ? personName : ANONYMOUS_SUBMITTER_LABEL;

  return [cancerLabel, submitter].filter(Boolean).join(TITLE_SEPARATOR);
}

/**
 * The case line that ends an inquiry group's title: the written clinical case, else the question
 * itself while one of the submitter's threads still holds only it. It is the part of the title that
 * runs long, so a hover shows it alone in full.
 *
 * The question stands in until a case is written so a card does not sit empty under its heading.
 * There is no field for the question's text, but a thread's last message is the question for as
 * long as it is the thread's only message. The form no longer asks for a diagnosis, so that field
 * is not used: it showed only what older inquiries or a seeding script typed there, such as "R/R".
 */
export function getQuestionGroupCaseLine(
  original: InquiryV2,
  originalGroup: readonly InquiryV2[] = [original]
) {
  if (original.clinicalCase?.status === 'written') {
    return original.clinicalCase.line.trim();
  }

  const questionThread = [original, ...originalGroup].find(
    (thread) => thread.lastMessageIndex === 0 && thread.lastMessagePreview?.trim()
  );

  return questionThread?.lastMessagePreview?.trim() ?? '';
}
